import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Menu,
  MenuItem,
  Typography,
  accordionSummaryClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { colors } from "@zapalacorp/tokens";

/**
 * Helper function to determine card background color based on task type
 * @param isPlaceholder - Whether the task is a placeholder
 * @param isVip - Whether the task is VIP
 * @returns Background color string
 */
const getCardBackgroundColor = (isPlaceholder?: boolean, isVip?: boolean): string => {
  if (isPlaceholder) {
    return colors.purple50;
  }

  if (isVip) {
    return colors.warning50;
  }

  return colors.white;
};

export const CardRoot = styled("div")(() => ({
  width: "100%",
  borderRadius: "12px",
  border: `1px solid ${colors.gray200}`,
  "&:not(:last-child)": {
    marginBottom: "16px",
  },
}));

export const CardHeader = styled(Box, {
  shouldForwardProp(propName) {
    return propName !== "isVip" && propName !== "isPlaceholder";
  },
})<{ isVip?: boolean; isPlaceholder?: boolean }>(({ isVip, isPlaceholder }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: getCardBackgroundColor(isPlaceholder, isVip),
  borderTopLeftRadius: "12px",
  borderTopRightRadius: "12px",
  overflow: "hidden",
}));

export const CardHeaderCompanySection = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "isVip";
  },
})(() => {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px 5px 12px",

    overflow: "hidden",
    color: colors.gray800,
    textOverflow: "ellipsis",
    fontFamily: "Manrope",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "16px",
    letterSpacing: "0.4px",
  };
});

export const CardChip = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "8px",
  padding: "4px 8px",
  border: `1px solid ${colors.gray200}`,
  width: "fit-content",
  borderRadius: "100px",
  backgroundColor: colors.gray50,

  textTransform: "uppercase",
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "0.5px",
});

export const CardHeaderDetailsSection = styled("div")(() => {
  return {
    padding: "8px 12px 0px 12px",
    width: "inherit",
    display: "flex",
    gap: "4px",
    flexDirection: "column",
  };
});

export const CardHeaderDetailsSectionLine = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "spaceBetween";
  },
})<{ spaceBetween?: boolean }>(({ spaceBetween }) => {
  return {
    width: "inherit",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    justifyContent: spaceBetween ? "space-between" : "flex-start",
  };
});

export const Heading = styled("h1")(() => ({
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: `${colors.gray600} !important`,
  margin: 0,
  flex: 1,
  overflow: "hidden",
}));

export const CardAccordion = styled(Accordion)(() => ({
  width: "100%",
  borderBottomLeftRadius: "12px !important",
  borderBottomRightRadius: "12px !important",
  "&.Mui-expanded": {
    marginTop: "0 !important",
  },
  "& .Mui-expanded": {
    minHeight: "0 !important",
  },
  "&:before": {
    display: "none",
  },
}));

export const CardAccordionSummary = styled(AccordionSummary, {
  shouldForwardProp(propName) {
    return propName !== "isVip" && propName !== "isPlaceholder";
  },
})<{ isVip?: boolean; isPlaceholder?: boolean }>(({ isVip, isPlaceholder }) => ({
  backgroundColor: isPlaceholder
    ? "rgba(244, 243, 255, 0.2)"
    : getCardBackgroundColor(false, isVip),

  [`:not(.${accordionSummaryClasses.expanded})`]: {
    borderBottomLeftRadius: "12px !important",
    borderBottomRightRadius: "12px !important",
  },
  "&.MuiAccordionSummary-root": {
    padding: "8px 12px",
  },
  "& .MuiAccordionSummary-content": {
    margin: "0 !important",
  },

  "& .MuiIconButton-edgeEnd": {
    marginTop: "-30px",
  },
}));

export const CustomerName = styled("p")(() => ({
  fontFamily: "Manrope",
  fontSize: "16px",
  fontWeight: 400,
  color: colors.gray600,
  margin: 0,
}));

export const CustomerAddress = styled("p")(() => ({
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  color: colors.gray500,
  margin: 0,
}));

export const TaskDescription = styled("p")(() => ({
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  color: colors.gray600,
  margin: 0,
  lineHeight: "16px",
  letterSpacing: "0.4px",
}));

export const TimeSection = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "disabled";
  },
})(({ disabled }: { disabled?: boolean }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "8px",
  cursor: disabled ? "not-allowed" : "pointer",
  borderRadius: "2px",
  padding: "2px 0",
  "&:hover": {
    backgroundColor: "#66708514",
  },
}));

export const TimeInfo = styled("span")(() => ({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.4px",
}));

export const BoxDetails = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  alignContent: "center",
  gap: "12px",
  flexWrap: "wrap",
}));

export const IconTypo = styled(Typography)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  color: colors.gray600,
}));

export const InfoDetails = styled("span")(() => ({
  fontFamily: "Manrope",
  fontSize: "12px",
  fontWeight: 400,
}));

export const CardAccordionDetails = styled(AccordionDetails)(() => ({
  display: "flex",
  flexDirection: "column",
  "&.MuiAccordionDetails-root": {
    padding: 0,
  },
}));

export const TaskResource = styled("div")(() => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
}));

export const CardItemSection = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: colors.gray100,
  padding: "8px",
  gap: "8px",
}));

export const ActionsFooter = styled("div", {
  shouldForwardProp(propName) {
    return propName !== "isVip" && propName !== "isPlaceholder";
  },
})<{ isVip?: boolean; isPlaceholder?: boolean }>(({ isVip, isPlaceholder }) => ({
  backgroundColor: getCardBackgroundColor(isPlaceholder, isVip),
  display: "flex",
  padding: "4px 12px",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  borderTop: `1px solid ${colors.gray200}`,
  borderBottomLeftRadius: "12px !important",
  borderBottomRightRadius: "12px !important",
}));

export const MoreOptionsMenu = styled(Menu)(() => ({
  "& .MuiPaper-root": {
    width: "200px",
    padding: "12px 0",
    borderRadius: "12px",
    background: colors.white,
    boxShadow: "0px 2px 6px 2px rgba(71, 84, 103, 0.15), 0px 1px 2px 0px rgba(71, 84, 103, 0.30)",
  },
  "& .MuiList-padding": {
    padding: 0,
  },
}));

export const OptionMenu = styled(MenuItem)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  "&.MuiMenuItem-root": {
    padding: "8px 12px",
  },
}));

export const MenuItemText = styled("span")(() => ({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0.25px",
}));

export const BookingDetails = styled(Box)(() => ({
  width: "100%",
}));

export const AccordionHeader = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
}));
export const FooterBox = styled(Box)({
  display: "flex",
  width: "50%",
  height: "100%",
  justifyContent: "flex-start",
  color: colors.gray600,
});
export const ParentBox = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
});

export const CommentsIconWrapper = styled("div")(() => ({
  position: "relative",
}));

export const MemberLeaderWrapper = styled("div")({
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  gap: 4,
});

export const MemberLeaderLabel = styled("span")({
  color: colors.gray600,
  fontFamily: "Manrope",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: 0.4,
});

export const MemberLeaderNameBadge = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: 100,
  backgroundColor: colors.gray100,
  "& span": {
    color: colors.gray600,
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0.5,
  },
});

export const useTaskCardStyles = makeStyles(() => ({
  statusChip: {
    height: "24px !important",
    maxWidth: 148,
    borderRadius: 100,
    padding: "4px 12px",
    "& > span": {
      fontFamily: "Manrope",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "16px",
      letterSpacing: 0.5,
    },
  },
  fullWidthDivider: {
    width: "calc(100% + 46px)", // Adjust this based on the padding/margin removed
    marginLeft: "-12px", // Move the divider to the left by half the padding
    marginRight: "-12px", // Move the divider to the right by half the padding
    marginTop: "8px", // Adjust spacing as needed
    marginBottom: "8px", // Adjust spacing as needed
  },
  iconSpacing: {
    fontSize: "16px",
    color: colors.gray600,
  },
}));
